import { Bloodwork, PanelRange, PanelField } from '.';
import { MathUtils } from '../utils';

export class PanelMarker {
  attribute: string;
  value: number;
  altValue?: any;
  percentFromMean?: number;
  range?: PanelRange;
  field?: PanelField;
  previousPanelMarker?: PanelMarker;

  constructor(panelMarker?: Partial<PanelMarker>) {
    if (!panelMarker) {
      return;
    }
    Object.keys(panelMarker).forEach(key => {
      this[key] = panelMarker[key];
    });
  }

  init?(
    ranges: PanelRange[],
    fields: PanelField[],
    bloodwork: Bloodwork,
    attributeOverride?: string,
  ): PanelMarker {
    this.range = PanelRange.findRange(ranges, this.attribute, bloodwork);
    this.field = PanelField.findField(
      fields,
      attributeOverride || this.attribute,
    );

    // Handle special case for dDimer
    if (this.attribute === 'dDimer' && bloodwork.client.isPregnant()) {
      this.field = new PanelField(this.field); // Make a copy since we dont want to change the original
      this.field.useRDWChart = false;
    }

    this.populatePercentFromMean();

    return this;
  }

  isFerritin?(): boolean {
    return this.attribute === 'ferritin';
  }

  isEGFR?(): boolean {
    return this.attribute === 'eGFR' || this.attribute === 'eGFRforAA';
  }

  isBilirubin?(): boolean {
    return this.attribute === 'bilirubin';
  }

  populatePercentFromMean?() {
    if (!this.range) {
      return;
    }
    if (this.field.useWBCPercentFromMean) {
      this.percentFromMean = this.wbcPercentFromMean();
    } else {
      // ((value - mean) / (alarmMax - mean)) * 100
      this.percentFromMean =
        ((this.value - this.range.mean) /
          (this.range.alarmMax - this.range.mean)) *
        100;
    }
  }

  altChartValue?(): number {
    if (!this.range) {
      return undefined;
    }

    // if value <= opt max, value x (166.67%/opt max) = % to plot
    if (this.isOptimal()) {
      return (this.value * 166.67) / this.range.optimalMax;
      // If value > opt max and <= lab max, (33.33/(lab max - opt max)) * (value-opt max)+166.67 = % to plot
    } else if (this.isGreaterThanOptimal() && this.isLab()) {
      return (
        (33.33 / (this.range.labMax - this.range.optimalMax)) *
          (this.value - this.range.optimalMax) +
        166.67
      );
      // If value > lab max and <= alarm max, (33.33/(alarm max - lab max))*(value - lab max)+200 = % to plot
    } else if (this.isGreaterThanLab() && this.isAlarm()) {
      return (
        (33.33 / (this.range.alarmMax - this.range.labMax)) *
          (this.value - this.range.labMax) +
        200
      );
      // If value > alarm max, (33.33%/1 std dev)*(value-alarm max)+233.33 = % to plot
    } else if (this.isGreaterThanAlarm()) {
      return (
        (33.33 / this.range.oneStdDev()) * (this.value - this.range.alarmMax) +
        233.33
      );
    }
    return -1;
  }

  altChartValueNoRedOrange?(): number {
    if (!this.range) {
      return undefined;
    }
    // if value < opt min, value x (100%/opt min) = % to plot
    if (this.isLessThanOptimal()) {
      return (this.value * 100) / this.range.optimalMin;
      // If value >= opt min and <= opt max, (66.67/(opt max - opt min)) * (value-opt min)+100 = % to plot
    } else if (this.isOptimal()) {
      return (
        (66.67 / (this.range.optimalMax - this.range.optimalMin)) *
          (this.value - this.range.optimalMin) +
        100
      );
      // If value > opt max and <= lab max, (33.33/(lab max - opt max)) * (value-opt max)+166.67 = % to plot
    } else if (this.isGreaterThanOptimal() && this.isLab()) {
      return (
        (33.33 / (this.range.labMax - this.range.optimalMax)) *
          (this.value - this.range.optimalMax) +
        166.67
      );
      // If value > lab max and <= alarm max, (33.33/(alarm max - lab max))*(value - lab max)+200 = % to plot
    } else if (this.isGreaterThanLab() && this.isAlarm()) {
      return (
        (33.33 / (this.range.alarmMax - this.range.labMax)) *
          (this.value - this.range.labMax) +
        200
      );
      // If value > alarm max, (33.33%/1 std dev)*(value-alarm max)+233.33 = % to plot
    } else if (this.isGreaterThanAlarm()) {
      return (
        (33.33 / this.range.oneStdDev()) * (this.value - this.range.alarmMax) +
        233.33
      );
    }
    return -1;
  }

  altChartValueNoRed?(): number {
    // if value < lab min, value x (66.67%/lab min) = % to plot
    if (this.isLessThanLab()) {
      return (this.value * 66.67) / this.range.labMin;
      // If value < opt min and >= lab min, (33.33/(opt max - lab min)) * (value-lab min)+66.67 = % to plot
    } else if (this.isLessThanOptimal() && this.isLab()) {
      return (
        (33.33 / (this.range.optimalMin - this.range.labMin)) *
          (this.value - this.range.labMin) +
        66.67
      );
      // If value >= opt min and <= opt max, (66.67/(opt max - opt min)) * (value-opt min)+100 = % to plot
    } else if (this.isOptimal()) {
      return (
        (66.67 / (this.range.optimalMax - this.range.optimalMin)) *
          (this.value - this.range.optimalMin) +
        100
      );
      // If value > opt max and <= lab max, (33.33/(lab max - opt max)) * (value-opt max)+166.67 = % to plot
    } else if (this.isGreaterThanOptimal() && this.isLab()) {
      return (
        (33.33 / (this.range.labMax - this.range.optimalMax)) *
          (this.value - this.range.optimalMax) +
        166.67
      );
      // If value > lab max and <= alarm max, (33.33/(alarm max - lab max))*(value - lab max)+200 = % to plot
    } else if (this.isGreaterThanLab() && this.isAlarm()) {
      return (
        (33.33 / (this.range.alarmMax - this.range.labMax)) *
          (this.value - this.range.labMax) +
        200
      );
      // If value > alarm max, (33.33%/1 std dev)*(value-alarm max)+233.33 = % to plot
    } else if (this.isGreaterThanAlarm()) {
      return (
        (33.33 / this.range.oneStdDev()) * (this.value - this.range.alarmMax) +
        233.33
      );
    }
    return -1;
  }

  eGFRChartValue?(): number {
    if (!this.range) {
      return undefined;
    }

    const shift = this.range.alarmMin - this.range.stdDev;

    if (this.value < this.range.alarmMin) {
      return (this.value / this.range.alarmMin) * 33.33;
    }

    return ((this.value - shift) * 33.33) / this.range.stdDev;
  }

  wbcPercentFromMean?(): number {
    if (!this.range || !this.hasValue()) {
      return undefined;
    }

    const alarmMaxMinusLabMax = this.range.alarmMax - this.range.labMax;
    const labMinMinusAlarmMin = this.range.labMin - this.range.alarmMin;

    // if value > alarmMax + (alarmMax - labMax),
    // this.percentFromMean = 133.33
    if (this.value > this.range.alarmMax + alarmMaxMinusLabMax) {
      return 133.33;
    }

    // if value > alarmMax and <= alarmMax + (alarmMax - labMax),
    // this.percentFromMean = (((value - alarmMax) / (alarmMax - labMax)) * 33.33) + 100
    if (
      this.isGreaterThanAlarm() &&
      this.value <= this.range.alarmMax + alarmMaxMinusLabMax
    ) {
      return (
        ((this.value - this.range.alarmMax) / alarmMaxMinusLabMax) * 33.33 + 100
      );
    }

    // if value > labMax and <= alarmMax,
    // this.percentFromMean =  (((value - labMax) / (alarmMax - labMax)) * 33.33) + 66.67
    if (this.isGreaterThanLab() && this.isAlarm()) {
      return (
        ((this.value - this.range.labMax) / alarmMaxMinusLabMax) * 33.33 + 66.67
      );
    }

    // if value > optimalMax and <= labMax
    // return ((value - optimalMax) / (labMax - optimalMax) * 33.33) + 33.33
    if (this.isGreaterThanOptimal() && this.isLab()) {
      const valueMinusOptimalMax = this.value - this.range.optimalMax;
      return (
        (valueMinusOptimalMax / (this.range.labMax - this.range.optimalMax)) *
          33.33 +
        33.33
      );
    }

    // if value <= optimalMax and >mean
    // return ((value - mean) / (optimalMax - mean) * 33.33)
    if (this.isOptimal() && this.value > this.range.mean) {
      return (
        ((this.value - this.range.mean) /
          (this.range.optimalMax - this.range.mean)) *
        33.33
      );
    }

    // if value >= optimalMin and <= mean
    // return ((value - mean) / (mean - optimalMin) * 33.33)
    if (this.isGreaterThanOrIsOptimal() && this.value <= this.range.mean) {
      return (
        ((this.value - this.range.mean) /
          (this.range.mean - this.range.optimalMin)) *
        33.33
      );
    }

    // if value < optimalMin and >= labMin
    // return ((value - optimalMin) / (optimalMin - labMin) * 33.33) - 33.33
    if (this.isLessThanOptimal() && this.isLab()) {
      return (
        ((this.value - this.range.optimalMin) /
          (this.range.optimalMin - this.range.labMin)) *
          33.33 -
        33.33
      );
    }

    // if value < labMin and >= alarmMin,
    // this.percentFromMean = (((value - labMin) / (labMin - alarmMin)) * 33.33) - 66.67
    if (this.isLessThanLab() && this.isAlarm()) {
      return (
        ((this.value - this.range.labMin) / labMinMinusAlarmMin) * 33.33 - 66.67
      );
    }

    // if value < alarmMin and >= alarmMin - (labMin - alarmMin),
    // this.percentFromMean = (((value - alarmMin) / (labMin - alarmMin)) * 33.33) - 100
    if (
      this.isLessThanAlarm() &&
      this.value >= this.range.alarmMin - labMinMinusAlarmMin
    ) {
      return (
        ((this.value - this.range.alarmMin) / labMinMinusAlarmMin) * 33.33 - 100
      );
    }

    // if value < alarmMin - (labMin - alarmMin), return -133.33
    if (this.value < this.range.alarmMin - labMinMinusAlarmMin) {
      return -133.33;
    }

    return undefined;
  }

  isLab?(): boolean {
    return this.range ? this.range.isLab(this.value) : false;
  }

  isNotLab?(): boolean {
    return this.hasValue() && !this.isLab();
  }

  isLessThanLab?(): boolean {
    return this.range ? this.range.isLessThanLab(this.value) : false;
  }

  isLessThanOrIsLab?(): boolean {
    return this.range
      ? this.range.isLessThanLab(this.value) || this.isLab()
      : false;
  }

  isGreaterThanLab?(): boolean {
    return this.range ? this.range.isGreaterThanLab(this.value) : false;
  }

  isGreaterThanOrIsLab?(): boolean {
    return this.range
      ? this.range.isGreaterThanLab(this.value) || this.range.isLab(this.value)
      : false;
  }

  isStricklyLessThanOptimal?(): boolean {
    return this.range ? this.range.isLessThanOptimal(this.value) : false;
  }

  /**
   * Less than Optimal Min and Greater than or Equal to LabMin
   * In other words, < Opt means < OptMin AND >= LabMin
   */
  isLessThanOptimal?(): boolean {
    return this.range
      ? this.isStricklyLessThanOptimal() && this.value >= this.range.labMin
      : false;
  }

  isLessThanOrIsOptimal?(): boolean {
    return this.isStricklyLessThanOptimal() || this.isOptimal();
  }

  isEmptyOrIsLessThanOrIsOptimal?(): boolean {
    return !this.hasValue() || this.isLessThanOrIsOptimal();
  }

  isGreaterThanOptimal?(): boolean {
    return this.range?.isGreaterThanOptimal(this.value);
  }

  isGreaterThanOrIsOptimal?(): boolean {
    return this.isGreaterThanOptimal() || this.isOptimal();
  }

  isOptimal?(): boolean {
    return this.range?.isOptimal(this.value);
  }

  isNotOptimal?(): boolean {
    return this.hasValue() && !this.isOptimal();
  }

  isAlarm?(): boolean {
    return this.range ? this.range.isAlarm(this.value) : false;
  }

  isLessThanAlarm?(): boolean {
    return this.range ? this.range.isLessThanAlarm(this.value) : false;
  }

  isGreaterThanAlarm?(): boolean {
    return this.range ? this.range.isGreaterThanAlarm(this.value) : false;
  }

  isLessThanMean?(): boolean {
    return this.range ? this.range.isLessThanMean(this.value) : false;
  }

  // <(Lab+Opt Min)/2 AND >= Lab Min
  isLessThanAvgMin?(): boolean {
    return this.range
      ? this.range.isLessThanAvgMin(this.value) && this.isGreaterThanOrIsLab()
      : false;
  }

  isGreaterThanAvgMin?(): boolean {
    return this.range ? this.range.isGreaterThanAvgMin(this.value) : false;
  }

  isGreaterThanOrEqualToAvgMin?(): boolean {
    return this.range
      ? this.range.isGreaterThanOrEqualToAvgMin(this.value)
      : false;
  }

  /**
   * >(Lab+Opt Max)/2 AND <= Lab Max
   */
  isGreaterThanAvgMax?(): boolean {
    return this.range
      ? this.range.isGreaterThanAvgMax(this.value) && this.isLessThanOrIsLab()
      : false;
  }

  isLessThanAvgMax?(): boolean {
    return this.range ? this.range.isLessThanAvgMax(this.value) : false;
  }

  isLessThanOrIsAvgMax?(): boolean {
    return this.range ? this.range.isLessThanOrIsAvgMax(this.value) : false;
  }

  //[>(Lab+Opt Max)/2 AND <= Lab Max] OR
  //[<(Lab+Opt Min)/2 AND >= Lab Min]
  isGreaterThanAvgMaxOrLessThanAvgMin?(): boolean {
    return this.isGreaterThanAvgMax() || this.isLessThanAvgMin();
  }

  // [<=(Lab+Opt Max)/2 AND > Opt Max] OR
  // [>=(Lab+Opt Min)/2 AND < Opt Min]
  isLessThanAvgMaxOrGreaterThanAvgMin?(): boolean {
    return (
      (this.isLessThanOrIsAvgMax() && this.isGreaterThanOptimal()) ||
      (this.isGreaterThanAvgMin() && this.isLessThanOptimal())
    );
  }

  hasValue?(): boolean {
    return this.value != null;
  }

  isBlank?(): boolean {
    return !this.hasValue();
  }

  isGreaterThanZero?(): boolean {
    return this.value > 0;
  }

  displayValue?(): string {
    return this.hasValue()
      ? '' + MathUtils.round(this.value, this.field.precision)
      : '';
  }
}
